import { useState, useEffect, useRef, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import {
  HiMiniInboxArrowDown,
  HiOutlinePaperAirplane,
  HiEye,
  //HiMagnifyingGlass,
  HiUserPlus,
  HiOutlineShieldCheck,
} from 'react-icons/hi2'
import { HiChevronDown } from 'react-icons/hi2'
import { Button } from '../../../components/Button'
// import { Input } from '../../../components/Input'
import { DemandaTable } from '../../../components/Table'
import { DemandsNavigation, type DemandType } from '../components/navigation'
import { FiltersFlyout } from '../components/FiltersFlyout'
import { useUnifiedDemandsLogic } from './logic'
import { DataRefreshStatus } from '../../../components/DataRefreshStatus'
import {
  Card,
  CardContent,
} from '../../../components/Cards'
import coverReceived from '../../assets/demandas-recebidas.png'
import coverSent from '../../assets/demandas-enviadas.png'
import coverWatching from '../../assets/observadores.png'
import coverToAssign from '../../assets/demandas-atibuicao.png'

export const UnifiedDemandsPage = () => {
  const location = useLocation()
  const [filtersOpen, setFiltersOpen] = useState(false)
  const filterButtonRef = useRef<HTMLButtonElement>(null)
  // const [viewMode] = useState<'table' | 'kanban'>('table');

  // Determinar o tipo atual baseado na URL
  const getCurrentTypeFromPath = useCallback((): DemandType => {
    if (location.pathname.includes('/received')) return 'received'
    if (location.pathname.includes('/sent')) return 'sent'
    if (location.pathname.includes('/watching')) return 'watching'
    if (location.pathname.includes('/toAssign')) return 'toAssign'
    if (location.pathname.includes('/authorization')) return 'authorization'
    if (location.pathname === '/demands') return 'received' // Default para /demands
    return 'received' // Default geral
  }, [location.pathname])

  const [currentType, setCurrentType] = useState<DemandType>(getCurrentTypeFromPath)

  // Atualizar tipo quando a URL mudar
  useEffect(() => {
    const newType = getCurrentTypeFromPath()
    if (newType !== currentType) {
      setCurrentType(newType)
    }
  }, [getCurrentTypeFromPath, currentType])

  // Handler for navigation type change
  const handleTypeChange = (type: DemandType) => {
    setCurrentType(type)
  }

  const {
    demandas,
    loading,
    error,
    stats,
    lastUpdated,
    autoRefresh,
    manualRefresh,
    smartRefresh,
    currentPage,
    totalPages,
    totalItems,
    itemsPerPage,
    goToPage,
    sorting,
    setSorting,
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    priorityFilter,
    setPriorityFilter,
    processoOptions,
    processoId,
    setProcessoId,
    handleView,
    handleEdit,
    clearFilters,
    // 🚀 Novos para carregamento lazy dos filtros
    loadFiltersIfNeeded,
    filtersLoaded,
  } = useUnifiedDemandsLogic(currentType)

  // Listener para refresh quando demanda for criada
  useEffect(() => {
    const handleDemandCreated = () => {
      // Refresh silencioso da lista
      smartRefresh.refresh()
    }

    window.addEventListener('demand-created', handleDemandCreated)
    return () => {
      window.removeEventListener('demand-created', handleDemandCreated)
    }
  }, [smartRefresh])

  // Carregar filtros quando o flyout for aberto
  useEffect(() => {
    if (filtersOpen && !filtersLoaded) {
      loadFiltersIfNeeded()
    }
  }, [filtersOpen, filtersLoaded, loadFiltersIfNeeded])

  // Configurações dinâmicas baseadas no tipo
  const getTypeConfig = (type: DemandType) => {
    switch (type) {
      case 'received':
        return {
          title: 'Demandas Recebidas',
          description:
            'Neste espaço você encontra todas as demandas que foram encaminhadas para o seu setor e atribuídas para você!',
          icon: <HiMiniInboxArrowDown className='w-6 h-6 text-yellow-600' />,
          iconBg: 'bg-yellow-100',
          cover: coverReceived,
        }
      case 'sent':
        return {
          title: 'Demandas Enviadas',
          description: 'Aqui estão todas as demandas que você criou e enviou para outros setores.',
          icon: <HiOutlinePaperAirplane className='w-6 h-6 text-blue-600' />,
          iconBg: 'bg-blue-100',
          cover: coverSent,
        }
      case 'watching':
        return {
          title: 'Demandas Observáveis',
          description:
            'Demandas que você está acompanhando e deseja receber notificações sobre mudanças.',
          icon: <HiEye className='w-6 h-6 text-green-600' />,
          iconBg: 'bg-green-100',
          cover: coverWatching,
        }
      case 'toAssign':
        return {
          title: 'Demandas em Atribuição',
          description: 'Demandas que chegaram ao setor e ainda não foram atribuídas.',
          icon: <HiUserPlus className='w-6 h-6 text-green-600' />,
          iconBg: 'bg-green-100',
          cover: coverToAssign,
        }
      case 'authorization':
        return {
          title: 'Autorizações Pendentes',
          description: 'Demandas que estão aguardando sua autorização para prosseguir.',
          icon: <HiOutlineShieldCheck className='w-6 h-6 text-purple-600' />,
          iconBg: 'bg-purple-100',
          cover: coverToAssign, // Usando o mesmo cover temporariamente
        }
      default:
        return {
          title: 'Demandas',
          description: 'Gerenciamento de demandas',
          icon: <HiMiniInboxArrowDown className='w-6 h-6 text-gray-600' />,
          iconBg: 'bg-gray-100',
          cover: coverReceived,
        }
    }
  }

  const typeConfig = getTypeConfig(currentType)

  if (error) {
    return (
      <div className='p-6'>
        <div className='bg-red-50 border border-red-200 rounded-lg p-4'>
          <div className='flex items-center'>
            <div className='text-red-700'>
              <strong>Erro ao carregar demandas:</strong> {error}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='h-[calc(100vh-82px)] flex flex-col overflow-hidden mb-0 p-2 space-y-6'>
      {/* Header Card with Background and Navigation */}
      <Card className='relative overflow-hidden'>
        <div
          className='absolute inset-0 bg-center bg-cover transition-all duration-500 ease-in-out opacity-20'
          style={{ backgroundImage: `url(${typeConfig.cover})` }}
        />
        {/* Gradiente superior para transição suave */}
        <div className='absolute inset-0 bg-gradient-to-b from-white/60 via-white/20 to-transparent dark:from-neutral-900/60 dark:via-neutral-900/20 dark:to-transparent'></div>
        {/* Gradiente inferior para transição suave */}
        <div className='absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white/80 to-transparent dark:from-neutral-900/80 dark:to-transparent'></div>
        <CardContent className='relative z-10 p-4'>
          <div className='flex items-start justify-between gap-4'>
            <div className='flex items-center space-x-3'>
              <div
                className={`p-2 ${typeConfig.iconBg} rounded-md transition-all duration-300 ease-in-out`}
              >
                {typeConfig.icon}
              </div>
              <div>
                <h1 className='text-xl font-bold text-gray-900 dark:text-white transition-all duration-300 ease-in-out'>
                  {typeConfig.title}
                </h1>
                <p className='text-sm text-gray-700 dark:text-gray-300 max-w-3xl transition-all duration-300 ease-in-out'>
                  {typeConfig.description}
                </p>
              </div>
            </div>
          </div>
          <div className='mt-4'>
            {/* Navigation and Search Controls in same row */}
            <div className='flex items-center justify-between gap-4'>
              {/* Navigation */}
              <DemandsNavigation currentType={currentType} onTypeChange={handleTypeChange} />

              {/* Search and Filter Controls */}
              <div className='flex items-center gap-2'>
                {/* <div className='relative'>
                  <HiMagnifyingGlass className='absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 w-3 h-3' />
                  <Input
                    placeholder='Buscar demandas...'
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className='pl-8 w-64 h-8 text-xs bg-white dark:bg-neutral-800 border-gray-200 dark:border-neutral-700'
                  />
                </div> */}
                <Button
                  ref={filterButtonRef}
                  type='button'
                  variant='outline'
                  className='flex items-center gap-1 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 px-2 py-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-neutral-700 transition-colors h-8'
                  onClick={() => setFiltersOpen(!filtersOpen)}
                  aria-expanded={filtersOpen}
                  disabled={false}
                >
                  <div
                    className={`transition-transform duration-300 ${filtersOpen ? 'rotate-180' : 'rotate-0'}`}
                  >
                    <HiChevronDown className='w-3 h-3' />
                  </div>
                  <span className='text-xs'>{filtersOpen ? 'Recolher' : 'Filtrar'}</span>
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filters Flyout */}
      <FiltersFlyout
        isOpen={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        anchorElement={filterButtonRef.current}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        priorityFilter={priorityFilter}
        setPriorityFilter={setPriorityFilter}
        processoId={processoId}
        setProcessoId={setProcessoId}
        processoOptions={processoOptions}
        clearFilters={clearFilters}
      />

      {/* Table Card */}
      <Card className='flex-1 flex flex-col overflow-hidden'>
        <CardContent className='flex-1 p-0 flex flex-col overflow-hidden'>
          {loading ? (
            <div className='flex items-center justify-center h-full p-8 '>
              <div className='flex items-center space-x-3'>
                <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600'></div>
                <div>
                  <p className='text-gray-900 dark:text-gray-100 font-medium'>
                    Carregando demandas...
                  </p>
                  <p className='text-gray-500 dark:text-gray-400 text-sm'>
                    Aguarde enquanto buscamos seus dados
                  </p>
                </div>
              </div>
            </div>
          ) : demandas.length === 0 ? (
            <div className='flex items-center justify-center h-full p-8 bg-transparent'>
              <div className='text-center max-w-sm bg-transparent'>
                <div className='mx-auto w-12 h-12 bg-transparent  rounded-full flex items-center justify-center mb-2'>
                  {typeConfig.icon}
                </div>
                <h3 className='text-lg font-semibold  text-gray-900 dark:text-gray-100 mb-2'>
                  {currentType === 'authorization'
                    ? 'Nenhuma autorização pendente'
                    : 'Nenhuma demanda encontrada'}
                </h3>
                <p className='text-sm text-gray-500 dark:text-gray-400'>
                  {currentType === 'authorization'
                    ? 'Não há demandas aguardando sua autorização no momento. ' +
                      'Você será notificado quando houver novas demandas para autorizar ou quando for sua vez na fila de autorização.'
                    : searchTerm || statusFilter || priorityFilter
                      ? 'Tente ajustar os filtros para encontrar o que procura.'
                      : `Você não possui demandas ${
                          currentType === 'received'
                            ? 'recebidas'
                            : currentType === 'sent'
                              ? 'enviadas'
                              : currentType === 'watching'
                                ? 'observáveis'
                                : currentType === 'toAssign'
                                  ? 'em atribuição'
                                  : 'disponíveis'
                        } no momento.`}
                </p>
              </div>
            </div>
          ) : (
            <DemandaTable
              demandas={demandas}
              onView={handleView}
              onEdit={handleEdit}
              maxHeight='calc(100vh - 350px)'
              sorting={sorting}
              onSortingChange={setSorting}
            />
          )}
        </CardContent>
      </Card>

      {/* Stats and Pagination Card */}
      <Card>
        <CardContent className='p-4'>
          <div className='flex items-center justify-between'>
            {/* Estatísticas à esquerda */}
            <div className='flex items-center space-x-4'>
              <div
                className='cursor-pointer hover:bg-gray-50 dark:hover:bg-neutral-700/50 px-2 py-1 rounded-md transition-all duration-200'
                onClick={() => setStatusFilter('EMATRIBUICAO')}
              >
                <span className='text-xs text-gray-600 dark:text-gray-400'>Em Atribuição:</span>
                <span className='ml-1 text-sm font-bold text-yellow-600 dark:text-yellow-500'>
                  {stats.emAtribuicao}
                </span>
              </div>

              <div className='h-3 w-px bg-gray-300 dark:bg-neutral-600'></div>

              <div
                className='cursor-pointer hover:bg-gray-50 dark:hover:bg-neutral-700/50 px-2 py-1 rounded-md transition-all duration-200'
                onClick={() => setStatusFilter('ATRIBUIDA')}
              >
                <span className='text-xs text-gray-600 dark:text-gray-400'>Atribuídas:</span>
                <span className='ml-1 text-sm font-bold text-blue-600 dark:text-blue-500'>
                  {stats.atribuidas}
                </span>
              </div>

              <div className='h-3 w-px bg-gray-300 dark:bg-neutral-600'></div>

              <div
                className='cursor-pointer hover:bg-gray-50 dark:hover:bg-neutral-700/50 px-2 py-1 rounded-md transition-all duration-200'
                onClick={() => setStatusFilter('EMPROGRESSO')}
              >
                <span className='text-xs text-gray-600 dark:text-gray-400'>Em Progresso:</span>
                <span className='ml-1 text-sm font-bold text-indigo-600 dark:text-indigo-500'>
                  {stats.emProgresso}
                </span>
              </div>

              <div className='h-3 w-px bg-gray-300 dark:bg-neutral-600'></div>

              <div
                className='cursor-pointer hover:bg-gray-50 dark:hover:bg-neutral-700/50 px-2 py-1 rounded-md transition-all duration-200'
                onClick={() => setStatusFilter('SOLUCIONADA')}
              >
                <span className='text-xs text-gray-600 dark:text-gray-400'>Solucionadas:</span>
                <span className='ml-1 text-sm font-bold text-purple-600 dark:text-purple-500'>
                  {stats.solucionadas}
                </span>
              </div>

              <div className='h-3 w-px bg-gray-300 dark:bg-neutral-600'></div>

              <div
                className='cursor-pointer hover:bg-gray-50 dark:hover:bg-neutral-700/50 px-2 py-1 rounded-md transition-all duration-200'
                onClick={() => setStatusFilter('DEVOLVIDA')}
              >
                <span className='text-xs text-gray-600 dark:text-gray-400'>Devolvidas:</span>
                <span className='ml-1 text-sm font-bold text-orange-600 dark:text-orange-500'>
                  {stats.devolvidas}
                </span>
              </div>

              <div className='h-3 w-px bg-gray-300 dark:bg-neutral-600'></div>

              <div
                className='cursor-pointer hover:bg-gray-50 dark:hover:bg-neutral-700/50 px-2 py-1 rounded-md transition-all duration-200'
                onClick={() => setStatusFilter('FINALIZADA')}
              >
                <span className='text-xs text-gray-600 dark:text-gray-400'>Finalizadas:</span>
                <span className='ml-1 text-sm font-bold text-green-600 dark:text-green-500'>
                  {stats.finalizadas}
                </span>
              </div>

              <div className='h-3 w-px bg-gray-300 dark:bg-neutral-600'></div>

              <div
                className='cursor-pointer hover:bg-gray-50 dark:hover:bg-neutral-700/50 px-2 py-1 rounded-md transition-all duration-200'
                onClick={() => clearFilters()}
              >
                <span className='text-xs text-gray-600 dark:text-gray-400'>Total:</span>
                <span className='ml-1 text-sm font-bold text-gray-900 dark:text-gray-100'>
                  {stats.total}
                </span>
              </div>

              <div className='h-3 w-px bg-gray-300 dark:bg-neutral-600'></div>

              {/* Última atualização com novo componente */}
              <DataRefreshStatus
                isRefreshing={smartRefresh.isRefreshing || autoRefresh.isRefreshing}
                lastUpdated={lastUpdated}
                refreshCount={smartRefresh.refreshCount}
                onManualRefresh={manualRefresh}
              />
            </div>

            {/* Paginação à direita - só mostra se houver mais de 10 itens */}
            {totalItems > itemsPerPage && (
              <div className='flex items-center space-x-2'>
                {/* Informações sobre os itens */}
                <div className='text-xs text-gray-600 dark:text-gray-400 mr-3'>
                  Mostrando{' '}
                  <span className='font-medium'>{(currentPage - 1) * itemsPerPage + 1}</span> a{' '}
                  <span className='font-medium'>
                    {Math.min(currentPage * itemsPerPage, totalItems)}
                  </span>{' '}
                  de <span className='font-medium'>{totalItems}</span> resultados
                </div>

                {/* Controles de paginação compactos */}
                <div className='flex items-center space-x-1'>
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage <= 1}
                    className='px-2 py-1 h-7 text-xs'
                  >
                    ←
                  </Button>

                  <span className='text-xs text-gray-600 dark:text-gray-400 px-2'>
                    {currentPage} de {totalPages}
                  </span>

                  <Button
                    variant='outline'
                    size='sm'
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    className='px-2 py-1 h-7 text-xs'
                  >
                    →
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
